import { useAddressBookIndexContext } from "../providers";
import { useAppContext } from "../../../AppProvider";
import { useTypes } from "../../../utilities/types";
import { faPlus } from "../../../utilities/fontawesomeIcons";

/**
 * Provides all configuration props for the AddressBook List Page,
 * including filters, pagination, creation modal, mass actions, and UI texts.
 *
 * This hook centralizes logic and keeps the List view fully declarative.
 */
export const useListProps = () => {
  const { service, ownerId, myAddress, setModalConfig } =
    useAddressBookIndexContext();

  const { dictionary, accessTokenPayload } = useAppContext();
  const { confirmationBoolOptions } = useTypes();

  const userIsOwner = accessTokenPayload.owner === ownerId;

  return {
    /** API endpoint path used by the ListView component */
    path: `/${service}-service/address-book`,

    /** Banner information (currently disabled) */
    banner: { enabled: false },

    /** Enable pagination */
    pagination: { enabled: true },

    /** Title of the page */
    title: userIsOwner
      ? dictionary.contacts.destinations_address_book
      : undefined,

    /** Create button configuration */
    create: {
      enabled: true,
      action: () =>
        setModalConfig({
          isOpen: true,
          action: "create",
          size: "md",
          title: dictionary.actions.add,
        }),
      title: dictionary.actions.add,
      icon: faPlus,
    },

    /** Enable checkbox selection for massive actions */
    selectRowsEnabled: { enabled: true },

    /** Filters applied to the list */
    filters: {
      name: "destination-address-book",
      preserve: true,
      list: [
        {
          fieldName: "myAddress",
          label: dictionary.words.public,
          type: "select",
          primary: true,
          options: confirmationBoolOptions,
          col: 2,
          value: {
            value: myAddress,
            label: myAddress ? dictionary.words.yes : dictionary.words.no,
            isBool: true,
          },
          hidden: true,
        },
        {
          fieldName: "ownerId",
          label: dictionary.words.public,
          type: "text",
          primary: true,
          col: 2,
          value: ownerId,
          hidden: true,
        },
      ],
    },

    /** Bulk (massive) actions applied to selected rows */
    massiveActions: [
      {
        label: dictionary.actions.delete_selected,
        className: "button-1 button-1-danger",
        onClick: (selectedRows: { id: string }[]) =>
          setModalConfig({
            isOpen: true,
            size: "sm",
            action: "delete",
            title: dictionary.actions.delete_selected,
            data: selectedRows.map((row) => row.id),
          }),
      },
    ],
  };
};
